/**
 * "Status: All ▾" / "Template: All ▾" checklist filter. See
 * FilterPopover.module.scss's header comment for why this isn't
 * theme/untitled/DropdownMenu (checkbox items that stay open on toggle,
 * not click-and-close action buttons). Shared between the widget index
 * pages (Calendars, Alerts) — fully generic, takes its options/labels as
 * props.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import Icon from "../../theme/untitled/icons/Icon";
import Checkbox from "../../theme/untitled/Checkbox/Checkbox";
import styles from "./FilterPopover.module.scss";

const OFFSET = 4;

const FilterPopover = ({ label, options, selectedValues, onChange }) => {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const applied = selectedValues.length > 0;
  const triggerLabel =
    selectedValues.length === 0
      ? `${label}: All`
      : selectedValues.length === 1
      ? `${label}: ${options.find((o) => o.value === selectedValues[0])?.label}`
      : `${label}: ${selectedValues.length} selected`;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;

    const reposition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const fitsBelow = spaceBelow >= panelHeight + OFFSET;
      const fitsAbove = spaceAbove >= panelHeight + OFFSET;
      const openUpward = !fitsBelow && (fitsAbove || spaceAbove > spaceBelow);

      setPanelStyle({
        position: "fixed",
        left: triggerRect.left,
        ...(openUpward
          ? { bottom: window.innerHeight - triggerRect.top + OFFSET }
          : { top: triggerRect.bottom + OFFSET }),
      });
    };

    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const toggleValue = (value) => {
    onChange(
      selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
    );
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={classNames(styles.trigger, { [styles["trigger--applied"]]: applied })}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {triggerLabel}
        <Icon name="ChevronDown" className={styles.chevron} />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={panelStyle || { position: "fixed", top: -9999, left: -9999 }}
          >
            {options.map((option) => (
              <div key={option.value} className={styles.option}>
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                  label={option.label}
                />
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default FilterPopover;
